import { PaymentInstrumentType } from '@primer-io/checkout-web'

import { Cohort, Color } from 'root-constants'

export const FIFTEEN_MINUTES_IN_SECONDS = 900

export const TEN_MINUTES = {
  IN_SECONDS: 600,
  AS_STRING: '10:00',
}

export const MONTHS_IN_A_YEAR = 12

export const DEFAULT_DYNAMIC_DISCOUNT = 50
export const ONE_HUNDRED_PERCENTAGE = 100
export const DECORATING_DISCOUNT = 0.01
export const CANCEL_OFFER_ADDITIONAL_DISCOUNT = 10

export const enum SubscriptionPeriod {
  MONTHLY = 1,
  QUARTERLY = 3,
  SEMI_ANNUALLY = 6,
  ANNUALLY = 12,
}

export const periodQuantityToWeek = {
  1: 4,
  3: 12,
  6: 26,
}

export const enum AnimationState {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

export const EMPTY_FIELD_ERROR = `can't be blank`

export const DEFAULT_CARDHOLDER_NAME = ' '

export const DEFAULT_ERROR_DESCRIPTION =
  'Please check your payment information or contact your bank and try again, or try a new payment method.'

export const enum StripeErrorCode {
  CARD_DECLINED = 'card_declined',
}

export const enum PaymentMethod {
  CREDIT_CARD = 'credit_card',
  APPLE_PAY = 'apple_pay',
  GOOGLE_PAY = 'google_pay',
  PAYPAL = 'pay_pal',
}

export const enum PaymentSystem {
  PAYPAL = 'paypal',
  PRIMER = 'primer',
  STRIPE = 'stripe',
}

export type TCardPaymentSystem = PaymentSystem.PRIMER | PaymentSystem.STRIPE

export const THREEDS_REDIRECT_SEARCH_PARAM = 'threeds'

export const enum StripeSoftDeclineReason {
  AUTHENTICATION_FAILURE = 'payment_intent_authentication_failure',
  DO_NOT_HONOR = 'do_not_honor',
  INSUFFICIENT_FUNDS = 'insufficient_funds',
  INVALID_AMOUNT = 'invalid_amount',
  PROCESSING_ERROR = 'processing_error',
  REENTER_TRANSACTION = 'reenter_transaction',
  SERVICE_NOT_ALLOWED = 'service_not_allowed',
  TRANSACTION_NOT_ALLOWED = 'transaction_not_allowed',
  CARD_VELOCITY_EXCEEDED = 'card_velocity_exceeded',
  GENERIC_DECLINE = 'generic_decline',
  TRY_AGAIN_LATER = 'try_again_later',
}

export const enum PaymentDeclineReason {
  STOLEN_CARD = 'stolen_card',
  LOST_CARD = 'lost_card',
  GENERIC_DECLINE = 'generic_decline',
}

export const STRIPE_SOFT_DECLINE_REASONS = [
  StripeSoftDeclineReason.AUTHENTICATION_FAILURE,
  StripeSoftDeclineReason.DO_NOT_HONOR,
  StripeSoftDeclineReason.INSUFFICIENT_FUNDS,
  StripeSoftDeclineReason.INVALID_AMOUNT,
  StripeSoftDeclineReason.PROCESSING_ERROR,
  StripeSoftDeclineReason.REENTER_TRANSACTION,
  StripeSoftDeclineReason.SERVICE_NOT_ALLOWED,
  StripeSoftDeclineReason.TRANSACTION_NOT_ALLOWED,
  StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED,
  StripeSoftDeclineReason.GENERIC_DECLINE,
  StripeSoftDeclineReason.TRY_AGAIN_LATER,
]

export const enum Currency {
  USD = 'usd',
  EUR = 'eur',
  GBP = 'gbp',
  NZD = 'nzd',
  MXN = 'mxn',
  JPY = 'jpy',
}

export const CURRENCY_SYMBOLS = {
  [Currency.USD]: '$',
  [Currency.EUR]: '€',
  [Currency.GBP]: '£',
  [Currency.NZD]: 'NZ$',
  [Currency.MXN]: 'MXN',
  [Currency.JPY]: '¥',
}

export const PURCHASE_DISCLAIMER = {
  [SubscriptionPeriod.MONTHLY]: 'purchase1.ratingInfo.monthlySubscription',
  [SubscriptionPeriod.QUARTERLY]: 'purchase1.ratingInfo.quarterlySubscription',
  [SubscriptionPeriod.SEMI_ANNUALLY]:
    'purchase1.ratingInfo.semiAnnuallySubscription',
  [SubscriptionPeriod.ANNUALLY]: 'purchase1.ratingInfo.annuallySubscription',
}

export const PURCHASE_DISCLAIMER_WITHOUT_TAXES = {
  [SubscriptionPeriod.MONTHLY]:
    'purchase1.ratingInfo.monthlySubscriptionWithoutTaxes',
  [SubscriptionPeriod.QUARTERLY]:
    'purchase1.ratingInfo.quarterlySubscriptionWithoutTaxes',
  [SubscriptionPeriod.SEMI_ANNUALLY]:
    'purchase1.ratingInfo.semiAnnuallySubscriptionWithoutTaxes',
  [SubscriptionPeriod.ANNUALLY]:
    'purchase1.ratingInfo.annuallySubscriptionWithoutTaxes',
}

export const CARDHOLDER_NAME_MIN_LENGTH = 1
export const enum PrimerErrorCode {
  CARD_REQUIRED = 'cardRequired',
  CARD_IS_INCOMPLETE = 'cardIncomplete',
  CARD_IS_INVALID = 'cardInvalid',
  EXPIRY_REQUIRED = 'expiryRequired',
  CARD_IS_EXPIRED = 'cardExpired',
  CARD_EXPIRY_YEAR_INVALID = 'expiryYearInvalid',
  CARD_EXPIRY_MONTH_INVALID = 'expiryMonthInvalid',
  CVV_IS_INCOMPLETE = 'cvvIncomplete',
  CVV_IS_INVALID = 'cvvInvalid3',
}

export const PRIMER_PAYMENT_ERRORS = {
  INSUFFICIENT_FUNDS: 'purchase1.insufficientFunds',
  PAYMENT_FAILED: 'purchase1.insufficientFunds',
  DECLINED: 'purchaseIntroOffer.declinedError',
  AUTHENTICATION_REQUIRED: 'purchaseIntroOffer.authenticationRequired',
  WITHDRAWAL_LIMIT_EXCEEDED: 'purchaseIntroOffer.withdrawLimit',
  EXPIRED_CARD: 'purchaseIntroOffer.expiredCard',
  COMMON_ERROR: 'login.somethingWentWrongError',
  ERROR: 'login.somethingWentWrongError',
  INVALID_CARD_NUMBER: 'login.somethingWentWrongError',
  LOST_OR_STOLEN_CARD: 'login.somethingWentWrongError',
  SUSPECTED_FRAUD: 'login.somethingWentWrongError',
  UNKNOWN: 'login.somethingWentWrongError',
  REFER_TO_CARD_ISSUER: 'login.somethingWentWrongError',
  DO_NOT_HONOR: 'login.somethingWentWrongError',
  ISSUER_TEMPORARILY_UNAVAILABLE: 'login.somethingWentWrongError',
}
export const PRIMER_CARD_NUMBER_ID = 'checkout-card-number-input'
export const PRIMER_CARD_EXPIRY_ID = 'checkout-card-expiry-input'
export const PRIMER_CARD_CVC_ID = 'checkout-card-cvv-input'
export const PRIMER_GOOGLE_PAY_ID = 'google-pay-button'
export const PRIMER_APPLE_PAY_ID = 'apple-pay-button'
export const PRIMER_PAYPAL_ID = 'paypal-button'

export const PRIMER_PAYMENT_METHODS_MAP = {
  [PaymentInstrumentType.CARD]: PaymentMethod.CREDIT_CARD,
  [PaymentInstrumentType.APPLE_PAY]: PaymentMethod.APPLE_PAY,
  [PaymentInstrumentType.GOOGLE_PAY]: PaymentMethod.GOOGLE_PAY,
  [PaymentInstrumentType.PAYPAL]: PaymentMethod.PAYPAL,
  [PaymentInstrumentType.PAYPAL_VAULTED]: PaymentMethod.PAYPAL,
}

export const enum PrimerFormVariants {
  DEFAULT = 'default',
  SEPARATED = 'separated',
}

export const PRIMER_FORM_COMMON_STYLES = {
  input: {
    base: {
      padding: '15px 12px',
      color: Color.DARK,
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      fontWeight: '500',
      height: '100%',
      lineHeight: '50px',
      fontSmoothing: 'antialiased',
      placeholder: {
        color: Color.LIGHT,
        fontWeight: '400',
      },
    },
    error: {
      color: Color.ERROR,
      caretColor: Color.DARK,
      focus: {
        color: Color.DARK,
        caretColor: Color.DARK,
      },
    },
  },
}

export const INITIAL_PRIMER_CONFIG = {
  card: {
    cardholderName: {
      required: false,
    },
  },
  style: {
    stylesheets: [
      {
        href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap',
      },
    ],
  },
}

export const CARDHOLDER_NAME_REGEX =
  /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ]+(([',. -][a-zA-ZáéíóúÁÉÍÓÚüÜñÑ ])?[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ]*)*$/

export const SUBSCRIPTION_DISCOUNTS = {
  DEFAULT: 50,
  INTRO_SALE: 60,
  INTRO3_SALE: 62,
  CANCEL_INTRO1: 50,
  INTRO6_SALE: 60,
}

export const INTRO_SALES_COHORTS_DISCOUNT_TEXT_MAP = {
  [Cohort.LUVLY_INTRO_SALE]: '50% Off Mega Sale',
  [Cohort.LUVLY_INTRO4_SALE]: '50% Off Mega Sale',
}
